import { useEffect, useMemo, useState } from 'react';
import Modal from '@/components/base/Modal';
import { useAppStore } from '@/store/AppStore';
import { SERVICES } from '@/mocks/constants';
import type { Appointment } from '@/types';

const inputClass =
  'h-10 w-full rounded-md border border-background-200 bg-white px-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100';
const labelClass = 'mb-1.5 block text-xs font-semibold text-foreground-600';

const TIMES = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];
const DURATIONS = ['1 saat', '2 saat', '3 saat', '4 saat', '5 saat', '6 saat', '7 saat', '8 saat'];

function parseTime(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
}

function durationHours(d: string): number {
  const n = parseInt(d, 10);
  return Number.isNaN(n) ? 2 : n;
}

interface AppointmentFormProps {
  open: boolean;
  onClose: () => void;
  prefillCustomer?: string;
  prefillService?: string;
  prefillQuoteId?: string;
  prefillQuoteAmount?: number;
  prefillLocation?: string;
}

export default function AppointmentForm({
  open,
  onClose,
  prefillCustomer,
  prefillService,
  prefillQuoteId,
  prefillQuoteAmount,
  prefillLocation,
}: AppointmentFormProps) {
  const { customers, quotes, appointments, teams, addAppointment } = useAppStore();

  const [customerName, setCustomerName] = useState(prefillCustomer ?? '');
  const [phone, setPhone] = useState('');
  const [quoteId, setQuoteId] = useState(prefillQuoteId ?? '');
  const [service, setService] = useState(prefillService ?? SERVICES[0]);
  const [location, setLocation] = useState(prefillLocation ?? '');
  const [address, setAddress] = useState('');
  const [date, setDate] = useState('26 Ağustos 2026');
  const [startTime, setStartTime] = useState('10:00');
  const [duration, setDuration] = useState('4 saat');
  const [team, setTeam] = useState('');
  const [customerNote, setCustomerNote] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [forceAssign, setForceAssign] = useState(false);
  const [error, setError] = useState('');

  const approvedQuotes = quotes.filter((q) => q.status === 'Onaylandı' || q.id === prefillQuoteId);

  function reset() {
    const c = customers.find((x) => x.name === (prefillCustomer ?? ''));
    setCustomerName(prefillCustomer ?? '');
    setPhone(c?.phone ?? '');
    setQuoteId(prefillQuoteId ?? '');
    setService(prefillService ?? SERVICES[0]);
    setLocation(prefillLocation ?? c?.region ?? '');
    setAddress(c?.address ?? '');
    setDate('26 Ağustos 2026');
    setStartTime('10:00');
    setDuration('4 saat');
    setTeam('');
    setCustomerNote('');
    setInternalNote('');
    setForceAssign(false);
    setError('');
  }

  useEffect(() => {
    if (open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefillCustomer, prefillQuoteId]);

  const teamConflicts = useMemo(() => {
    const start = parseTime(startTime);
    const end = start + durationHours(duration) * 60;
    return teams.map((t) => {
      const clash = appointments.some((a) => {
        if (a.team !== t.name || a.date !== date) return false;
        const as = parseTime(a.startTime);
        const ae = as + durationHours(a.duration) * 60;
        return start < ae && as < end;
      });
      return { team: t, clash };
    });
  }, [teams, appointments, date, startTime, duration]);

  const selectedConflict = teamConflicts.find((t) => t.team.name === team)?.clash ?? false;

  function handleSelectCustomer(name: string) {
    setCustomerName(name);
    const c = customers.find((x) => x.name === name);
    if (c) {
      setPhone(c.phone);
      setLocation(c.region);
      setAddress(c.address);
    }
  }

  function handleSelectQuote(id: string) {
    setQuoteId(id);
    const q = quotes.find((x) => x.id === id);
    if (q) {
      setService(q.service);
      setLocation(q.location);
      if (q.customerName) setCustomerName(q.customerName);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!customerName || !date || !team) {
      setError('Müşteri, tarih ve ekip zorunludur.');
      return;
    }
    if (selectedConflict && !forceAssign) {
      setError('Bu ekip seçilen saat aralığında başka bir işe atanmış. Başka ekip seçin veya "Yine de Ata" deyin.');
      return;
    }
    const linkedQuote = quotes.find((q) => q.id === quoteId);
    const appointment: Appointment = {
      id: `RDV-2026-${String(Date.now()).slice(-4)}`,
      customerName,
      phone,
      service,
      location,
      address,
      date,
      startTime,
      duration,
      team,
      status: 'Planlandı',
      quoteAmount: prefillQuoteAmount ?? linkedQuote?.amount ?? 0,
      quoteId: quoteId || '',
      customerNote: customerNote || '',
      internalNote: internalNote || '',
    };
    addAppointment(appointment);
    reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Randevu Oluştur"
      maxWidth="max-w-2xl"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-background-200 px-4 py-2 text-sm font-semibold text-foreground-700 hover:bg-background-100"
          >
            Vazgeç
          </button>
          <button
            type="submit"
            form="appointment-form"
            className="rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600"
          >
            Randevuyu Kaydet
          </button>
        </>
      }
    >
      <form id="appointment-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Müşteri</label>
            <select className={inputClass} value={customerName} onChange={(e) => handleSelectCustomer(e.target.value)}>
              <option value="">Müşteri seç</option>
              {customers.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Onaylı Teklif</label>
            <select className={inputClass} value={quoteId} onChange={(e) => handleSelectQuote(e.target.value)}>
              <option value="">Seçilmedi</option>
              {approvedQuotes.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.id} — {q.customerName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Hizmet</label>
            <select className={inputClass} value={service} onChange={(e) => setService(e.target.value)}>
              {SERVICES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Bölge / Konum</label>
            <input className={inputClass} value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Beykoz / İstanbul" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>Adres</label>
            <input className={inputClass} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Tam adres" />
          </div>
          <div>
            <label className={labelClass}>Tarih</label>
            <input className={inputClass} value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Başlangıç</label>
              <select className={inputClass} value={startTime} onChange={(e) => setStartTime(e.target.value)}>
                {TIMES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Süre</label>
              <select className={inputClass} value={duration} onChange={(e) => setDuration(e.target.value)}>
                {DURATIONS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Team availability */}
        <div>
          <label className={labelClass}>Ekip</label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {teamConflicts.map(({ team: t, clash }) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setTeam(t.name);
                  setForceAssign(false);
                }}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  team === t.name
                    ? 'border-primary-400 bg-primary-50'
                    : 'border-background-200 bg-white hover:bg-background-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-foreground-900">{t.name}</span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      clash ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
                    }`}
                  >
                    {clash ? 'Çakışma Var' : 'Müsait'}
                  </span>
                </div>
                <div className="mt-1 truncate text-xs text-foreground-500">
                  {clash ? 'Saat aralığı dolu' : 'Uygun'}
                </div>
              </button>
            ))}
          </div>

          {team && selectedConflict && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm font-semibold text-amber-800">
                {team} seçilen saat aralığında başka bir işe atanmış.
              </p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setTeam('');
                    setForceAssign(false);
                  }}
                  className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100"
                >
                  Başka Ekip Seç
                </button>
                <button
                  type="button"
                  onClick={() => setForceAssign(true)}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                    forceAssign ? 'bg-red-500 text-white' : 'border border-red-300 text-red-700 hover:bg-red-50'
                  }`}
                >
                  Yine de Ata
                </button>
              </div>
            </div>
          )}
        </div>

        <div>
          <label className={labelClass}>Müşteri Notu</label>
          <textarea
            className="h-16 w-full resize-none rounded-md border border-background-200 bg-white px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            value={customerNote}
            onChange={(e) => setCustomerNote(e.target.value)}
            placeholder="Müşteriye iletilecek not"
          />
        </div>
        <div>
          <label className={labelClass}>Dahili Not</label>
          <textarea
            className="h-16 w-full resize-none rounded-md border border-background-200 bg-white px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            value={internalNote}
            onChange={(e) => setInternalNote(e.target.value)}
            placeholder="Ekip için dahili not"
          />
        </div>

        {prefillQuoteAmount ? (
          <p className="rounded-md bg-accent-100 px-3 py-2 text-xs font-semibold text-accent-800">
            Onaylı teklif tutarı aktarıldı: {prefillQuoteAmount.toLocaleString('tr-TR')} TL
          </p>
        ) : null}

        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{error}</p>}
      </form>
    </Modal>
  );
}